/**
 * `<pg-table>` — tabela reutilizável e genérica.
 *
 * Dados são passados via PROPRIEDADES JS (não atributos HTML, por
 * serem estruturas complexas): `columns` e `rows`. O componente nunca
 * sabe o que os dados representam — quem sabe que uma linha é um
 * "Pedido" é a tela que consome o componente.
 *
 * Uso:
 * ```js
 * const tabela = document.querySelector('pg-table');
 * tabela.columns = [{ key: 'nome', label: 'Nome' }, { key: 'total', label: 'Total', align: 'right' }];
 * tabela.rows = [{ nome: 'Ana', total: 'R$ 100,00' }];
 * ```
 *
 * Evento emitido ao clicar uma linha: `pg-row-click`, com
 * `detail: { row, index }`.
 */
export class PgTable extends HTMLElement {
    static get observedAttributes() {
        return ['empty-message'];
    }
    root;
    _columns = [];
    _rows = [];
    constructor() {
        super();
        this.root = this.attachShadow({ mode: 'open' });
    }
    get columns() {
        return this._columns;
    }
    set columns(valor) {
        this._columns = valor;
        this.render();
    }
    get rows() {
        return this._rows;
    }
    set rows(valor) {
        this._rows = valor;
        this.render();
    }
    connectedCallback() {
        this.render();
    }
    attributeChangedCallback() {
        this.render();
    }
    render() {
        const mensagemVazia = this.getAttribute('empty-message') ?? 'Nenhum registro encontrado';
        const cabecalho = this._columns
            .map((coluna) => `<th style="text-align:${coluna.align ?? 'left'}">${escaparHtml(coluna.label)}</th>`)
            .join('');
        const linhas = this._rows
            .map((linha, indice) => `
          <tr data-indice="${indice}" tabindex="0">
            ${this._columns
            .map((coluna) => {
            const valor = linha[coluna.key];
            const conteudo = ehBadgeCell(valor)
                ? `<pg-badge tone="${valor.tom}">${escaparHtml(valor.texto)}</pg-badge>`
                : ehBadgeListCell(valor)
                    ? `<span class="lista-badges">${valor.badges.map((b) => `<pg-badge tone="${b.tom}">${escaparHtml(b.texto)}</pg-badge>`).join('')}</span>`
                    : escaparHtml(String(valor ?? ''));
            return `<td style="text-align:${coluna.align ?? 'left'}">${conteudo}</td>`;
        })
            .join('')}
          </tr>`)
            .join('');
        this.root.innerHTML = `
      <style>
        :host { display: block; font-family: var(--pg-font-familia-base, system-ui); }
        table {
          width: 100%;
          border-collapse: collapse;
          font-size: var(--pg-font-size-sm, 0.875rem);
          color: var(--pg-color-texto-primario, #f2f3f5);
        }
        thead th {
          text-align: left;
          font-weight: var(--pg-font-weight-semibold, 600);
          color: var(--pg-color-texto-secundario, #9aa1ab);
          padding: var(--pg-space-3, 12px) var(--pg-space-4, 16px);
          border-bottom: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          font-size: var(--pg-font-size-xs, 0.75rem);
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        tbody td {
          padding: var(--pg-space-3, 12px) var(--pg-space-4, 16px);
          border-bottom: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          font-variant-numeric: tabular-nums;
        }
        .lista-badges { display: inline-flex; flex-wrap: wrap; gap: var(--pg-space-2, 8px); }
        tbody tr:hover {
          background: var(--pg-color-bg-hover, #22262b);
          cursor: pointer;
        }
        tbody tr:focus-visible {
          outline: none;
          background: var(--pg-color-bg-hover, #22262b);
          box-shadow: inset var(--pg-shadow-foco, 0 0 0 3px rgba(47,140,255,0.35));
        }
        .vazio {
          padding: var(--pg-space-6, 32px);
          text-align: center;
          color: var(--pg-color-texto-secundario, #9aa1ab);
          font-size: var(--pg-font-size-sm, 0.875rem);
        }
      </style>
      ${this._rows.length === 0
            ? `<div class="vazio">${escaparHtml(mensagemVazia)}</div>`
            : `<table part="table">
              <thead><tr>${cabecalho}</tr></thead>
              <tbody>${linhas}</tbody>
            </table>`}
    `;
        this.root.querySelectorAll('tbody tr').forEach((tr) => {
            const emitirClique = () => {
                const indice = Number(tr.dataset.indice);
                this.dispatchEvent(new CustomEvent('pg-row-click', {
                    bubbles: true,
                    composed: true,
                    detail: { row: this._rows[indice], index: indice },
                }));
            };
            tr.addEventListener('click', emitirClique);
            // Linhas são focáveis (`tabindex`) para navegação por teclado — Enter
            // e Espaço replicam o mesmo comportamento do clique do mouse, nunca
            // um comportamento à parte.
            tr.addEventListener('keydown', (evento) => {
                if (evento.key !== 'Enter' && evento.key !== ' ')
                    return;
                evento.preventDefault();
                emitirClique();
            });
        });
    }
}
function ehBadgeCell(valor) {
    return typeof valor === 'object' && valor !== null && 'texto' in valor && 'tom' in valor;
}
function ehBadgeListCell(valor) {
    return typeof valor === 'object' && valor !== null && 'badges' in valor && Array.isArray(valor.badges);
}
function escaparHtml(valor) {
    const div = document.createElement('div');
    div.textContent = valor;
    return div.innerHTML;
}
customElements.define('pg-table', PgTable);
//# sourceMappingURL=pg-table.js.map